/* eslint-disable react/jsx-no-literals -- pre-i18n migration; replace literals with t() and remove this directive (see docs/i18n-migration-plan.md) */
/**
 * Notification Bell — Header dropdown with real-time WebSocket updates
 * Shows unread count badge, recent notifications dropdown, mark-read support
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import notificationService from '@services/notificationService';
import useRealtimeNotifications from '@hooks/useRealtimeNotifications';
import { useAuth } from '@hooks/useAuth';

const NotificationBell = () => {
  const [recent, setRecent] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  // Route the bell to the notifications page that lives under THIS role's
  // layout, so an admin clicking "View All" doesn't get dropped into the
  // couple's wedding sidebar.
  const notificationsPath =
    ['admin', 'super_admin'].includes(user?.role) ? '/admin/notifications'
    : user?.role === 'vendor'                      ? '/vendor/notifications'
    : user?.role === 'coordinator'                 ? '/coordinator/notifications'
    : user?.role === 'venue_owner'                 ? '/venue/notifications'
    : '/dashboard/notifications';

  // ─── Real-time notifications via WebSocket ───
  const { notifications: liveNotifs, unreadCount, setUnreadCount, markRead, markAllRead, connected } = useRealtimeNotifications();

  // Initial load of unread count — fixed 60s interval, independent of WS state
  // to prevent interval stacking when WebSocket reconnects
  useEffect(() => {
    const load = async () => {
      try { const r = await notificationService.getUnreadCount(); setUnreadCount(r.data?.count || 0); } catch {}
    };
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, [setUnreadCount]);

  // Merge live notifications into recent list
  useEffect(() => {
    if (liveNotifs.length > 0 && open) {
      setRecent(prev => {
        const ids = new Set(prev.map(n => n.id));
        const fresh = liveNotifs.filter(n => !ids.has(n.id));
        return [...fresh, ...prev].slice(0, 10);
      });
    }
  }, [liveNotifs, open]);

  const toggleDropdown = async () => {
    if (!open) {
      try { const r = await notificationService.getAll({ limit: 8, unread_only: 'true' }); setRecent(r.data?.notifications || []); } catch {}
    }
    setOpen(!open);
  };

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const goToNotification = async (n) => {
    if (!n.isRead) {
      try { await notificationService.markRead(n.id); markRead(n.id); } catch {}
    }
    setOpen(false);
    // `actionUrl` is what the backend Notification model persists; older
    // notifications used `link`. Fall back through both before the generic page.
    navigate(n.link || n.actionUrl || notificationsPath);
  };

  const handleMarkAllRead = async () => {
    try { await notificationService.markAllRead(); markAllRead(); setRecent(prev => prev.map(n => ({ ...n, isRead: true }))); } catch {}
  };

  const timeAgo = (d) => {
    const s = (Date.now() - new Date(d).getTime()) / 1000;
    if (s < 60) return 'now';
    if (s < 3600) return `${Math.floor(s / 60)}m`;
    if (s < 86400) return `${Math.floor(s / 3600)}h`;
    return `${Math.floor(s / 86400)}d`;
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={toggleDropdown}
        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px 10px', position: 'relative', fontSize: '1.2rem' }}>
        <i className="bi bi-bell" style={{ color: '#6B7280' }} />
        {unreadCount > 0 && (
          <Badge bg="danger" pill
            style={{ position: 'absolute', top: 0, right: 2, fontSize: '0.55rem', minWidth: 16, animation: 'pulse 2s infinite' }}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </Badge>
        )}
        {/* WebSocket connection dot */}
        {connected && (
          <span style={{ position: 'absolute', bottom: 4, right: 4, width: 6, height: 6, borderRadius: '50%', background: '#22C55E', display: 'block' }} />
        )}
      </button>

      {open && (
        <div style={{ position: 'absolute', top: '100%', right: 0, width: 360, background: '#fff', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.15)', zIndex: 9999, overflow: 'hidden' }}>
          <div className="d-flex justify-content-between align-items-center px-3 py-2"
            style={{ background: '#F8F4FF', borderBottom: '1px solid #E5E7EB' }}>
            <span className="fw-bold small">
              Notifications
              {connected && <span style={{ color: '#22C55E', fontSize: '0.5rem', marginLeft: 4 }}>● Live</span>}
            </span>
            <div className="d-flex gap-2 align-items-center">
              {unreadCount > 0 && (
                <button className="btn btn-link btn-sm p-0 small" style={{ fontSize: '0.65rem' }} onClick={handleMarkAllRead}>
                  Mark all read
                </button>
              )}
              <button className="btn btn-link btn-sm p-0 small" onClick={() => { setOpen(false); navigate(notificationsPath); }}>
                View All
              </button>
            </div>
          </div>
          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            {recent.length > 0 ? recent.map(n => (
              <div key={n.id} onClick={() => goToNotification(n)}
                style={{
                  padding: '10px 14px', borderBottom: '1px solid #F3F4F6', cursor: 'pointer',
                  background: n.isRead ? '#fff' : '#F8F4FF', transition: 'background 0.2s'
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#F0ECFF'; }}
                onMouseLeave={e => { e.currentTarget.style.background = n.isRead ? '#fff' : '#F8F4FF'; }}>
                <div className="d-flex gap-2 align-items-start">
                  <span style={{ fontSize: '1rem' }}>{n.icon || '🔔'}</span>
                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <div className="d-flex justify-content-between">
                      <span className="fw-bold" style={{ fontSize: '0.75rem' }}>{n.title}</span>
                      <small className="text-muted" style={{ fontSize: '0.6rem' }}>{timeAgo(n.createdAt)}</small>
                    </div>
                    {n.message && (
                      <p className="text-muted mb-0" style={{ fontSize: '0.7rem', lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {n.message}
                      </p>
                    )}
                  </div>
                  {!n.isRead && <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#9B30FF', flexShrink: 0, marginTop: 4 }} />}
                </div>
              </div>
            )) : (
              <div className="text-center py-4">
                <span style={{ fontSize: '1.5rem' }}>✨</span>
                <p className="text-muted small mt-1 mb-0">All caught up!</p>
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`@keyframes pulse { 0%,100%{transform:scale(1)} 50%{transform:scale(1.15)} }`}</style>
    </div>
  );
};

export default NotificationBell;
